import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { opsProposalEndpoints } from '@/lib/api/endpoints';
import { opsProposalKeys } from '@/lib/queryKeys';
import type { Proposal } from '@/types/proposal';

export type SendChannels = ('email' | 'sms')[];

/** The two ways a proposal page can ask the customer to accept. */
export type ProposalSendApprovalMethod = 'SIGNATURE' | 'ONLINE';

export type SendProposalInput = {
  id: string;
  channels?: SendChannels;
  /** Omitted keeps whatever the proposal already holds. */
  approvalMethod?: ProposalSendApprovalMethod | null;
};

/**
 * Sends a proposal to the customer via the operations microservice.
 */
export function useSendProposal() {
  const queryClient = useQueryClient();

  return useApiMutation<Proposal, SendProposalInput>({
    mutationFn: ({ id, channels, approvalMethod }) =>
      apiPost<Proposal>(operationsClient, opsProposalEndpoints.send(id), {
        channels,
        ...(approvalMethod ? { approval_method: approvalMethod } : {}),
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: opsProposalKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: opsProposalKeys.detail(data.id) });
      toast.success('Proposal sent');
    },
  });
}
